import React, { Component } from "react";
import { matchPath, Redirect, Route, Router, Switch } from "react-router-dom";
import withIdleTimer from "../common/WithIdleTimer";
import { invalidateSessionAndRedirect } from "../common/InvalidateSession";
import {
  ACCESSORIES_KEY,
  CANADA_ENGLISH,
  DELIVERY_TOOLS,
  DELIVERY_TOOLS_QR,
  ENGLISH,
  LINCOLN_ENGLISH,
  MODEL_E_KEY,
  MODEL_E_ORDER_DETAILS_KEY,
  PAGE,
  QUERY,
  SALESCODE_KEY,
  SEARCH,
} from "../common/Constants";
import NADealer from "./pure/NADealer";
import { applyBrandChange } from "./pure/changeBrand";
import GlobalNavBar from "../shared/globalNavBar/GlobalNavBar";
import AccessoriesWrapper from "../components/accessories/AccessoriesWrapper";
import { fetchDealerGroup } from "../shared/hooks/useDealerGroup";
import { publish } from "../common/PubSub";
import ErrorScreen from "../errors/ErrorScreen";
import DealerDashboard from "../components/dashboard/dealerDashBoard/DealerDashboard";
import ProtectedRoute from "../common/ProtectedRoute";
import Footer from "../shared/Footer/Footer";
import LocalizedInfoClient from "../clients/LocalizedInfoClient";
import { logMsg } from "../common/Logger";
import PageError from "../errors/PageError";
import "../styles/sharedStyles.scss";
import BusinessSettings from "../components/businessSetting/BusinessSettings";
import Help from "../components/help/Help";
import PendingActionClient from "../clients/PendingActionClient";
import {
  HAS_EMP_ONBOARD_ROLE,
  IS_CANADA_MARKET,
  IS_FORD_BRAND,
  IS_USA_MARKET,
} from "../utils/EmpUtil";
import {
  canAccessDeliveryTools,
  isCanadianRestrictedDeliveryToolsUser,
  isLincolnDeliveryPilotUser,
} from "../utils/lincolnDeliveryPilot";
import {
  AGREEMENT_ACCEPTED_STATUS,
  hasCustomerHandlingRole,
  isCanadianPreorderUser,
  usesDealerExperienceRoutes,
} from "../utils/canadianPreorderExperience";
import PreOrderUEVClient from "../clients/PreOrderUEVClient";
import NADealerReporting from "../components/reporting/NADealerReporting";
import { withTranslation } from "react-i18next";
import Reporting from "../components/reporting/Reporting";
import DeliveryTools from "../components/deliveryTools/DeliveryTools";
import * as AnalyticsService from "../shared/services/analytics/AnalyticsService";
import SearchResultsWrapper from "../components/customerHandling/search/SearchResultsWrapper";
import ReleaseNotes from "../components/releaseNotes/ReleaseNotes";
import styles from "./App.module.scss";
import OrderDetailsPage from "../orders/order-details/OrderDetailsPage";
import { ModalProvider } from "../shared/components/EMPModal/ModalContext";
import Orders from "../orders/Orders";
import OnboardingPageWithStripe from "../onboarding/OnboardingPageWithStripe";
import CanadianPreOrderEnrollmentPage from "../components/preOrderEnrollment/CanadianPreOrderEnrollmentPage";
import { hasPartsOnlyRole } from "../shared/hooks/usePartsOnlyRole";
import { UserContext } from "../shared/context/userContext";
import {
  getMarketBrandPath,
  getMarketBrandRoute,
  MARKET_BRAND_ROUTE_PREFIX,
} from "../utils/marketBrandUtils";

// Routes that now live under /:marketBrand (e.g. /usa-ford/dashboard). Bare
// versions can still arrive from bookmarks or MSAL restoring the pre-login URL.
const LEGACY_MARKET_BRAND_PATHS = [
  "/dashboard",
  "/customer-handling",
  "/dealer-reporting",
  "/delivery-tools",
  "/onboarding",
];

class NADealerApp extends Component {
  static contextType = UserContext;

  constructor(props) {
    super(props);
    this.state = {
      renderApp: false,
      preorderStatusLoading: false,
      user: new NADealer(this.props.userDetails, this.props.history),
      dealerGroupError: false,
      shouldNavigationBeRestricted: false,
    };
    this.displayPage = sessionStorage.getItem(PAGE);
    this.searchQuery = sessionStorage.getItem(QUERY);
    this.salesCode = sessionStorage.getItem(SALESCODE_KEY);
    this.localizedInfo = {};
    this.dealerChanged = this.dealerChanged.bind(this);
    this.getPendingActions = this.getPendingActions.bind(this);
  }

  componentDidMount() {
    AnalyticsService.initAnalytics();
    // Initialize UserContext with basic user details immediately
    this.initializeUserContext();
    this.getLocalizedInfo();
  }

  componentWillUnmount() {
    this.unlisten?.();
  }

  initializeUserContext = () => {
    const { updateUser } = this.context;
    if (updateUser && this.props.userDetails) {
      // Initialize with basic user details from props to prevent null errors
      updateUser(this.props.userDetails);
    }
  };

  updateUserContext = () => {
    const { updateUser } = this.context;
    if (updateUser && this.state.user && this.state.user.permissions) {
      // Create a serializable user object for the context
      const userContextData = {
        ...this.props.userDetails, // Include original userDetails for backward compatibility
        // Override with current state values (only if they exist)
        salesCode: this.state.user.salesCode,
        brand: this.state.user.brand,
        market: this.state.user.market,
        dealerName: this.state.user.dealerName,
        dealerId: this.state.user.dealerId,
        userType: this.state.user.userType,
        roles: this.state.user.roles,
        permissions: this.state.user.permissions,
        // Add any other relevant user properties that components might need
        currentDealer: this.state.user.currentDealer,
        dealerGroup: this.state.user.dealerGroup,
        preorderAgreementStatus: this.state.user.preorderAgreementStatus,
      };
      updateUser(userContextData);
    }
  };

  getLocalizedInfo() {
    LocalizedInfoClient.getLocalizedInfo(this.state.user)
      .then((response) => {
        this.localizedInfo = {
          ...this.localizedInfo,
          ...response,
        };
      })
      .catch((error) => logMsg("the error is: ", error))
      .finally(() => {
        this.setUserLanguage();
        this.getDealerGroup();
      });
  }

  setUserLanguage() {
    const savedLanguage = sessionStorage.getItem("lang");

    if (savedLanguage) {
      this.lang = savedLanguage;
      return;
    }

    // Determine default language based on market and brand
    this.lang = this.determineDefaultLanguage();

    // Update i18n and user language code for specific cases
    if (
      IS_USA_MARKET(this.state.user.market) &&
      !IS_FORD_BRAND(this.state.user.brand)
    ) {
      this.updateLanguageSettings(this.lang);
    }
  }

  determineDefaultLanguage() {
    if (IS_USA_MARKET(this.state.user.market)) {
      return IS_FORD_BRAND(this.state.user.brand) ? ENGLISH : LINCOLN_ENGLISH;
    }

    return IS_CANADA_MARKET(this.state.user.market) ? CANADA_ENGLISH : ENGLISH;
  }

  updateLanguageSettings(language) {
    this.props.i18n.changeLanguage(language);
    this.state.user.setLanguageCode(this.props.i18n.language);
  }

  getDealerGroup = () => {
    this.props.hideOrShow(true);
    fetchDealerGroup(
      this.state.user,
      this.salesCode,
      sessionStorage.getItem(PAGE)
    )
      .then(({ data, selectedDealer }) => {
        this.state.user.setDealerGroup(data);
        if (selectedDealer) {
          sessionStorage.setItem("dealerName", selectedDealer.dealerName);
          this.updateCurrentDealer(selectedDealer, true);
        }
        this.props.hideOrShow(false);
      })
      .catch((error) => {
        this.setState({ dealerGroupError: true });
        console.log(error);
      })
      .finally(() => {
        const hasPendingActionPermission =
          this.state.user.permissions.rolePermissions.hasOwnProperty([
            "pendingActionQueue:view",
          ]);
        if (
          IS_CANADA_MARKET(this.state.user.market) &&
          hasPendingActionPermission
        ) {
          this.determinePendingActionStatuses();
          this.getPendingActions().finally(() =>
            this.performFinalActions.call(this)
          );
        } else {
          this.performFinalActions();
        }

        this.props.hideOrShow(false);
      });
  };

  loadPreorderAgreementStatus = async () => {
    const { user } = this.state;
    let status;
    const commonId = user.commonId || user.commonDealerId;
    this.preorderStatusKey = this.getPreorderStatusKey(user);
    if (isCanadianPreorderUser(user, AGREEMENT_ACCEPTED_STATUS) && commonId) {
      try {
        const data = await PreOrderUEVClient.getAgreementStatus(commonId);
        status = data?.status;
      } catch (error) {
        logMsg(`Unable to load pre-order agreement status: ${error?.message}`);
      }
    }
    const currentUser = this.state.user;
    if (commonId === (currentUser.commonId || currentUser.commonDealerId)) {
      currentUser.preorderAgreementStatus = status;
    }
  };

  getPreorderStatusKey = (user) =>
    isCanadianPreorderUser(user, AGREEMENT_ACCEPTED_STATUS)
      ? user.commonId || user.commonDealerId
      : "";

  handleAgreementStatusChange = (dealerCommonId, status) => {
    const { user } = this.state;
    if (
      dealerCommonId !== (user.commonId || user.commonDealerId) ||
      user.preorderAgreementStatus === status
    ) {
      return;
    }
    user.preorderAgreementStatus = status;
    this.setState({ user }, this.updateUserContext);
  };

  refreshPreorderAgreementStatus = () => {
    const { user, renderApp } = this.state;
    const statusKey = this.getPreorderStatusKey(user);
    if (
      !renderApp ||
      this.getPreorderStatusKey(user) === this.preorderStatusKey
    ) {
      return;
    }
    if (!isCanadianPreorderUser(user, AGREEMENT_ACCEPTED_STATUS)) {
      this.preorderStatusKey = "";
      user.preorderAgreementStatus = undefined;
      return;
    }
    this.loadPreorderAgreementStatus().then(() => {
      if (this.getPreorderStatusKey(this.state.user) === statusKey) {
        this.setState(
          { user: this.state.user, preorderStatusLoading: false },
          this.updateUserContext
        );
      }
    });
  };

  performFinalActions() {
    if (!isCanadianPreorderUser(this.state.user, AGREEMENT_ACCEPTED_STATUS)) {
      this.completeFinalActions();
      return;
    }
    return this.loadPreorderAgreementStatus().then(() =>
      this.completeFinalActions()
    );
  }

  completeFinalActions() {
    this.syncMarketBrandPath();
    this.setNavigationRestriction();
    this.setState({ renderApp: true, preorderStatusLoading: false }, () => {
      // Initialize UserContext after everything is loaded and state is set
      this.updateUserContext();
      // Subscribe after the Router mounts so a nested replace cannot leave it
      // displaying the original, mismatched location from the outer navigation.
      if (!this.unlisten) {
        this.unlisten = this.props.history.listen(() => {
          this.syncMarketBrandPath();
          this.setNavigationRestriction();
        });
      }
    });
    this.props.hideOrShow(false);
  }

  setNavigationRestriction = () => {
    const currentPath = this.props?.history?.location?.pathname || "/";
    const isAllowedPath =
      this.isCurrentPathAllowedToShowHeaderAndFooter(currentPath);
    const newRestrictionValue = !isAllowedPath;

    // Only update state if the value actually changed
    if (this.state.shouldNavigationBeRestricted !== newRestrictionValue) {
      this.setState({ shouldNavigationBeRestricted: newRestrictionValue });
    }
  };

  isCurrentPathAllowedToShowHeaderAndFooter = (currentPathName) => {
    const paths = [
      "/dashboard",
      "/pricing/accessories",
      "/",
      "/dealer-reporting",
      "/business-settings",
      "/help",
      "/error",
      "/reporting",
      "/delivery-tools",
      "/delivery-tools/download",
      "/delivery-tools/tasks",
      "/pre-order-enrollment",
      "/ecommerce-releases",
      "/customer-handling",
      "/orders",
      "/onboarding",
      "/pre-order-enrollment",
    ];

    const marketBrandPaths = [
      "/dashboard",
      "/dealer-reporting",
      "/delivery-tools",
      "/delivery-tools/download",
      "/delivery-tools/tasks",
      "/customer-handling",
      "/onboarding",
    ];

    if (
      paths.includes(currentPathName) ||
      marketBrandPaths.some((path) =>
        matchPath(currentPathName, {
          path: getMarketBrandRoute(path),
          exact: true,
        })
      )
    ) {
      return true;
    } else {
      const allowedOrderDetails = matchPath(currentPathName, {
        path: `/dealer/:commonId/customer-reservations/order-details/:id`,
      });

      const allowedPurchaseDetails = matchPath(currentPathName, {
        path: `/dealer/:commonId/customer-reservations/purchase-details/:id`,
      });

      const allowedModelEOrderDetails = matchPath(currentPathName, {
        path: `/dealer/:commonId/customer-reservations/model-e-details/:id`,
      });

      const allowedCustomerHandlingTabs = matchPath(currentPathName, {
        path: getMarketBrandRoute("/customer-handling"),
      });

      const allowedSearchResultsWrapper = matchPath(currentPathName, {
        path: `/search/:keyword`,
      });

      const allowedNewOrderDetails = matchPath(currentPathName, {
        path: getMarketBrandRoute("/customer-handling/:orderId"),
      });
      return (
        !!allowedOrderDetails ||
        !!allowedPurchaseDetails ||
        !!allowedModelEOrderDetails ||
        !!allowedCustomerHandlingTabs ||
        !!allowedSearchResultsWrapper ||
        !!allowedNewOrderDetails
      );
    }
  };

  updateCurrentDealer(dealer, showAll = false) {
    const user = this.state.user.updateCurrentDealer(dealer, showAll);
    if (this.getPreorderStatusKey(user) !== this.preorderStatusKey) {
      user.preorderAgreementStatus = undefined;
    }
    this.setState(
      {
        user,
        preorderStatusLoading:
          !!this.getPreorderStatusKey(user) &&
          this.getPreorderStatusKey(user) !== this.preorderStatusKey,
      },
      () => {
        this.state.user.setLanguageCode(this.props.i18n.language);
        this.syncMarketBrandPath();
        // Update UserContext after dealer change
        this.updateUserContext();
        this.refreshPreorderAgreementStatus();
      }
    );
  }

  dealerChanged(dealer, showAll = false) {
    this.updateCurrentDealer(dealer, showAll);
  }

  changeBrand = (brandCode) => {
    const isUsaMarket = IS_USA_MARKET(this.state.user.market);
    const { updatedUser, language } = applyBrandChange(
      this.state.user,
      brandCode,
      { isUsaMarket, includeFordHubMdlr: true, includeLanguage: true }
    );
    if (this.getPreorderStatusKey(updatedUser) !== this.preorderStatusKey) {
      updatedUser.preorderAgreementStatus = undefined;
    }
    this.setState(
      {
        user: updatedUser,
        preorderStatusLoading:
          !!this.getPreorderStatusKey(updatedUser) &&
          this.getPreorderStatusKey(updatedUser) !== this.preorderStatusKey,
      },
      () => {
        this.syncMarketBrandPath();
        this.updateUserContext();
        this.refreshPreorderAgreementStatus();
        publish("brandChanged", brandCode);
      }
    );
    if (language) {
      this.updateLanguageSettings(language);
    }
  };

  determinePendingActionStatuses() {
    let hasNscCompletedBasicPAs = false;
    PendingActionClient.getHasNscCompletedPA(this.state.user, 1)
      .then((response) => {
        hasNscCompletedBasicPAs = response;
      })
      .then(() => {
        const user = this.state.user;
        user.hasNscCompletedBasicPAs = hasNscCompletedBasicPAs;
        this.setState({ user });
      });
  }

  async getPendingActions(dealer = this.state.user, updatePAOnly = true) {
    await PendingActionClient.getDealerPendingActionsNotifications(dealer).then(
      (data) => {
        if (updatePAOnly) {
          this.state.user.updatePendingActions(data.pendingActions);
        } else {
          this.setState({ user: dealer }, () => {
            this.state.user.updatePendingActions(data.pendingActions);
          });
        }
      }
    );
  }

  updateDealerPhoneAndEmail(dealer, email, phone) {
    const index = this.state.user.dealerGroup.indexOf(
      this.state.user.dealerGroup.filter(
        (d) => d.completeDealerId === dealer.dealerId
      )[0]
    );

    const dealerGroupCopy = JSON.parse(
      JSON.stringify(this.state.user.dealerGroup)
    );
    dealerGroupCopy[index].emailAddress = email;
    dealerGroupCopy[index].phoneNumber = phone;

    this.state.user.setDealerGroup(dealerGroupCopy);
  }

  updateDealerEmpMembership(dealer) {
    const index = this.state.user.dealerGroup.indexOf(
      this.state.user.dealerGroup.filter(
        (d) => d.completeDealerId === dealer.completeDealerId
      )[0]
    );

    const dealerGroup = this.state.user.dealerGroup;
    dealerGroup[index].status = dealer.status;

    this.state.user.setDealerGroup(dealerGroup);
  }

  isMainViewingSDLR = (dealer) => {
    // Returns true if a main dealer is currently viewing an SDLR
    return this.state.user.isLoggedInUserMDLR && dealer.dealerType === "SDLR";
  };

  getDealerPath = (path) =>
    getMarketBrandPath(this.state.user.market, this.state.user.brand, path);

  getCanadianPreorderLandingPath = () => {
    const permissions = this.state.user.permissions?.rolePermissions || {};
    const savedRoutes = {
      [MODEL_E_KEY]: this.getDealerPath("/customer-handling"),
      [MODEL_E_ORDER_DETAILS_KEY]: this.getDealerPath(
        `/customer-handling/${this.searchQuery}`
      ),
    };
    if (savedRoutes[this.displayPage]) return savedRoutes[this.displayPage];
    if (
      this.displayPage === ACCESSORIES_KEY &&
      Object.hasOwn(permissions, "accessoriesPage")
    ) {
      return "/pricing/accessories";
    }
    if (this.displayPage === SEARCH && this.searchQuery) {
      return `/search/${this.searchQuery}`;
    }
    if ([DELIVERY_TOOLS, DELIVERY_TOOLS_QR].includes(this.displayPage)) {
      return this.getDealerPath("/delivery-tools/tasks");
    }

    return this.getDealerPath("/dashboard");
  };

  syncMarketBrandPath = () => {
    const { history } = this.props;
    const { location } = history;
    const match = matchPath(location.pathname, {
      path: MARKET_BRAND_ROUTE_PREFIX,
    });

    if (!match) {
      return;
    }

    const suffix = location.pathname.slice(match.url.length);
    const pathname = this.getDealerPath(suffix);

    if (pathname !== location.pathname) {
      history.replace({ ...location, pathname });
    }
  };

  renderLegacyMarketBrandRedirect() {
    return (
      <Route
        path={LEGACY_MARKET_BRAND_PATHS}
        render={({ location }) => (
          <Redirect
            to={{
              ...location,
              pathname: this.getDealerPath(location.pathname),
            }}
          />
        )}
      />
    );
  }

  renderPartsOnlyRoutes() {
    // Restricted routing for users with EMP_NONVEHECOMMERCE_PARTSONLY role
    return (
      <Switch>
        <ProtectedRoute
          exact
          path={getMarketBrandRoute("/onboarding")}
          component={withIdleTimer(OnboardingPageWithStripe)}
          perform={["onboarding:nonvehicle"]}
          permissions={this.state.user.permissions.rolePermissions}
          customprops={{
            user: this.state.user,
            localizedInfo: this.localizedInfo,
            onDealerChanged: this.dealerChanged,
            lang: this.lang,
          }}
          parentProps={this.props}
          condition={true}
        />
        <Route
          exact
          path="/error"
          render={() => <ErrorScreen {...this.props} />}
        />
        <Route
          path="/ecommerce-releases"
          component={withIdleTimer(() => (
            <ReleaseNotes {...this.props} user={this.state.user} />
          ))}
        />
        <Route
          path="/"
          exact
          render={() => (
            <Redirect
              to={{
                pathname: this.getDealerPath("/onboarding"),
              }}
            />
          )}
        />
        <Route
          render={() => (
            <PageError
              code="404 Not Found"
              body="This page does not exist.  Please go back and try again."
            />
          )}
        />
      </Switch>
    );
  }

  render() {
    const isCanadianPreorder = isCanadianPreorderUser(this.state.user);
    const isCanadianCustomerHandlingDealer =
      IS_CANADA_MARKET(this.state.user.market) &&
      hasCustomerHandlingRole(this.state.user);
    const isCanadianPreOrderUser =
      IS_CANADA_MARKET(this.state.user.market) &&
      HAS_EMP_ONBOARD_ROLE(this.state.user) &&
      !isCanadianPreorder;
    const isCanadianDeliveryTools = isCanadianRestrictedDeliveryToolsUser(
      this.state.user
    );
    const isLincolnPilot = isLincolnDeliveryPilotUser(this.state.user);
    const deliveryToolsPath = this.getDealerPath("/delivery-tools/tasks");
    return this.state.renderApp && !this.state.preorderStatusLoading ? (
      !this.state.user.salesCode && !this.state.dealerGroupError ? (
        invalidateSessionAndRedirect()
      ) : (
        <Router history={this.props.history}>
          <ModalProvider>
            <section className={styles.fullHeightLayout}>
              <div className={styles.fullHeightItem}>
                {!this.state.dealerGroupError &&
                  !this.state.shouldNavigationBeRestricted && (
                    <GlobalNavBar
                      {...this.props}
                      urlPath={window.location.pathname}
                      user={this.state.user}
                      changeBrand={this.changeBrand}
                      i18n={this.props.i18n}
                      roles={this.state.user.roles}
                      hideToolbar={isCanadianPreOrderUser}
                    />
                  )}
              </div>
              <div className={styles.fullHeightItem}>
                {isCanadianPreOrderUser ? (
                  <Switch>
                    <Route
                      exact
                      path="/pre-order-enrollment"
                      render={() => (
                        <CanadianPreOrderEnrollmentPage
                          user={this.state.user}
                          onAgreementStatusChange={
                            this.handleAgreementStatusChange
                          }
                        />
                      )}
                    />
                    <Route
                      render={() => <Redirect to="/pre-order-enrollment" />}
                    />
                  </Switch>
                ) : hasPartsOnlyRole(this.state.user) ? (
                  this.renderPartsOnlyRoutes()
                ) : (
                  <Switch>
                    {this.renderLegacyMarketBrandRedirect()}

                    {isCanadianPreorder && (
                      <Route
                        exact
                        path="/pre-order-enrollment"
                        render={() => (
                          <Redirect
                            to={this.getCanadianPreorderLandingPath()}
                          />
                        )}
                      />
                    )}

                    {isLincolnPilot && (
                      <Route
                        exact
                        path={[
                          "/pricing",
                          "/pricing/accessories",
                          "/business-settings",
                          "/dealer/customer-reservations",
                          getMarketBrandRoute("/onboarding"),
                        ]}
                        render={() => (
                          <Redirect
                            to={{
                              pathname: this.getDealerPath("/dashboard"),
                            }}
                          />
                        )}
                      />
                    )}

                    {isLincolnPilot && (
                      <Route
                        exact
                        path="/reporting"
                        render={() => (
                          <Redirect
                            to={{
                              pathname: this.getDealerPath("/dealer-reporting"),
                            }}
                          />
                        )}
                      />
                    )}

                    {isCanadianDeliveryTools && (
                      <Route
                        exact
                        path={[
                          getMarketBrandRoute("/dashboard"),
                          "/pricing/accessories",
                          getMarketBrandRoute("/customer-handling"),
                          "/reporting",
                        ]}
                        render={() => (
                          <Redirect
                            to={{
                              pathname: deliveryToolsPath,
                            }}
                          />
                        )}
                      />
                    )}

                    {isCanadianDeliveryTools && (
                      <Route
                        path={getMarketBrandRoute(
                          "/customer-handling/:orderId"
                        )}
                        render={() => (
                          <Redirect
                            to={{
                              pathname: deliveryToolsPath,
                            }}
                          />
                        )}
                      />
                    )}

                    {isCanadianDeliveryTools && (
                      <Route
                        path="/search/:keyword"
                        render={() => (
                          <Redirect
                            to={{
                              pathname: deliveryToolsPath,
                            }}
                          />
                        )}
                      />
                    )}

                    <ProtectedRoute
                      exact
                      path={getMarketBrandRoute("/dashboard")}
                      component={withIdleTimer(DealerDashboard)}
                      perform={isCanadianPreorder ? [] : ["dashBoardPage"]}
                      permissions={this.state.user.permissions.rolePermissions}
                      customprops={{
                        user: this.state.user,
                        onDealerChanged: this.dealerChanged,
                        lang: this.lang,
                      }}
                      parentProps={this.props}
                      condition={true}
                    />
                    <ProtectedRoute
                      exact
                      path="/pricing/accessories"
                      component={withIdleTimer(AccessoriesWrapper)}
                      perform={["accessoriesPage"]}
                      permissions={this.state.user.permissions.rolePermissions}
                      customprops={{
                        user: this.state.user,
                        changeBrand: this.changeBrand,
                        onDealerChanged: this.dealerChanged,
                        localizedInfo: this.localizedInfo,
                        lang: this.lang,
                      }}
                      parentProps={this.props}
                      condition={true}
                    />
                    <ProtectedRoute
                      exact
                      path={getMarketBrandRoute("/customer-handling")}
                      component={withIdleTimer(Orders)}
                      perform={
                        isCanadianPreorder ? [] : ["customerHandlingPage"]
                      }
                      permissions={this.state.user.permissions.rolePermissions}
                      customprops={{
                        user: this.state.user,
                        localizedInfo: this.localizedInfo,
                        onDealerChanged: this.dealerChanged,
                        lang: this.lang,
                      }}
                      parentProps={this.props}
                      condition={true}
                    />

                    <Route
                      path={getMarketBrandRoute("/customer-handling/:orderId")}
                    >
                      <OrderDetailsPage />
                    </Route>

                    <ProtectedRoute
                      exact
                      path="/business-settings"
                      component={withIdleTimer(BusinessSettings)}
                      perform={["businessSettingsPage"]}
                      permissions={this.state.user.permissions.rolePermissions}
                      condition={true}
                      customprops={{
                        ...this.props,
                        isMainViewingSDLR: this.isMainViewingSDLR,
                        getPendingActions: this.getPendingActions.bind(this),
                        user: this.state.user,
                        lang: this.lang,
                        dealerGroup: this.state.user.dealerGroup,
                        localizedInfo: this.localizedInfo,
                        updateDealerPhoneAndEmail:
                          this.updateDealerPhoneAndEmail.bind(this),
                        updateDealerEmpMembership:
                          this.updateDealerEmpMembership.bind(this),
                        onDealerChanged: this.dealerChanged.bind(this),
                        permissions:
                          this.state.user.permissions.rolePermissions,
                      }}
                    />
                    <ProtectedRoute
                      exact
                      path={getMarketBrandRoute("/dealer-reporting")}
                      component={withIdleTimer(NADealerReporting)}
                      perform={
                        isCanadianPreorder ? [] : ["dealerReporting:view"]
                      }
                      permissions={this.state.user.permissions.rolePermissions}
                      customprops={{
                        user: this.state.user,
                        lang: this.lang,
                      }}
                      parentProps={this.props}
                      condition={usesDealerExperienceRoutes(this.state.user)}
                    />
                    <ProtectedRoute
                      exact
                      path="/reporting"
                      component={withIdleTimer(
                        isCanadianCustomerHandlingDealer
                          ? NADealerReporting
                          : Reporting
                      )}
                      perform={isCanadianPreorder ? [] : ["reportingPage"]}
                      permissions={this.state.user.permissions.rolePermissions}
                      customprops={{
                        user: this.state.user,
                        lang: this.lang,
                      }}
                      parentProps={this.props}
                      condition={usesDealerExperienceRoutes(this.state.user)}
                    />
                    <ProtectedRoute
                      path={getMarketBrandRoute("/delivery-tools")}
                      component={withIdleTimer(DeliveryTools)}
                      perform={[]}
                      permissions={this.state.user.permissions.rolePermissions}
                      customprops={{ user: this.state.user, lang: this.lang }}
                      parentProps={this.props}
                      condition={canAccessDeliveryTools(this.state.user)}
                    />
                    <ProtectedRoute
                      exact
                      path={getMarketBrandRoute("/onboarding")}
                      component={withIdleTimer(OnboardingPageWithStripe)}
                      perform={["onboarding:nonvehicle"]}
                      permissions={this.state.user.permissions.rolePermissions}
                      customprops={{
                        user: this.state.user,
                        localizedInfo: this.localizedInfo,
                        onDealerChanged: this.dealerChanged,
                        lang: this.lang,
                      }}
                      parentProps={this.props}
                      condition={true}
                    />
                    <Route
                      path="/ecommerce-releases"
                      component={withIdleTimer(() => (
                        <ReleaseNotes {...this.props} user={this.state.user} />
                      ))}
                    />
                    <ProtectedRoute
                      exact
                      path="/search/:keyword"
                      component={withIdleTimer(SearchResultsWrapper)}
                      perform={[]}
                      permissions={this.state.user.permissions.rolePermissions}
                      customprops={{
                        user: this.state.user,
                      }}
                      parentProps={this.props}
                      condition={true}
                    />
                    <Route
                      exact
                      path="/"
                      render={() =>
                        isCanadianDeliveryTools ? (
                          <Redirect
                            to={{
                              pathname: deliveryToolsPath,
                            }}
                          />
                        ) : isCanadianPreorder ? (
                          <Redirect
                            to={this.getCanadianPreorderLandingPath()}
                          />
                        ) : hasPartsOnlyRole(this.state.user) ? (
                          <Redirect
                            to={{
                              pathname: this.getDealerPath("/onboarding"),
                            }}
                          />
                        ) : this.displayPage === ACCESSORIES_KEY ? (
                          <Redirect
                            to={{
                              pathname: "/pricing/accessories",
                            }}
                          />
                        ) : this.displayPage === MODEL_E_KEY ? (
                          <Redirect
                            to={{
                              pathname:
                                this.getDealerPath("/customer-handling"),
                            }}
                          />
                        ) : this.displayPage === SEARCH ? (
                          <Redirect
                            to={{
                              pathname: `/search/${this.searchQuery}`,
                            }}
                          />
                        ) : this.displayPage === MODEL_E_ORDER_DETAILS_KEY ? (
                          <Redirect
                            to={{
                              pathname: this.getDealerPath(
                                `/customer-handling/${this.searchQuery}`
                              ),
                            }}
                          />
                        ) : this.displayPage === DELIVERY_TOOLS ? (
                          <Redirect
                            to={{
                              pathname: this.getDealerPath("/delivery-tools"),
                            }}
                          />
                        ) : this.displayPage === DELIVERY_TOOLS_QR ? (
                          <Redirect
                            to={{
                              pathname: this.getDealerPath("/delivery-tools"),
                              search: "?qrcid=qr-deliverydaytool",
                            }}
                          />
                        ) : (
                          <Redirect
                            to={{
                              pathname: this.getDealerPath("/dashboard"),
                            }}
                          />
                        )
                      }
                    />
                    <Route
                      exact
                      path="/help"
                      component={withIdleTimer(() => (
                        <Help {...this.props} market={this.state.user.market} />
                      ))}
                    />
                    <Route
                      exact
                      path="/error"
                      render={() => <ErrorScreen {...this.props} />}
                    />
                    <Route
                      render={() => (
                        <PageError
                          code="404 Not Found"
                          body="This page does not exist.  Please go back and try again."
                        />
                      )}
                    />
                  </Switch>
                )}
              </div>
              <div className={styles.fullHeightItem}>
                {!this.state.shouldNavigationBeRestricted && (
                  <span className="noPrint">
                    <Footer
                      user={this.state.user}
                      footerTitle={this.props.t("Footer.baseTitle")}
                      helpSupportTitle={this.props.t("Footer.help")}
                      permissions={this.state.user?.permissions || {}}
                      showUpcomingChanges={IS_USA_MARKET(
                        this.state.user.market
                      )}
                    />
                  </span>
                )}
              </div>
            </section>
          </ModalProvider>
        </Router>
      )
    ) : (
      ""
    );
  }
}

export default withTranslation("emp")(NADealerApp);
